import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import RunTimeline from "./RunTimeline";
import WorkflowControls from "./WorkflowControls";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { seoApi } from "@/lib/seoApi";
import { ArrowLeft, Copy, Check, Pencil, X, Save, History, ExternalLink, Newspaper, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function RunList() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await seoApi.get("/seo/workflows/runs?limit=200");
    setRows(r.data);
    setSelected(new Set());
  }
  useEffect(() => { load(); }, []);

  function toggleAll(e) {
    if (e.target.checked) setSelected(new Set(rows.map((r) => r.id)));
    else                    setSelected(new Set());
  }
  function toggle(id) {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  async function bulkAction(kind) {
    const ids = [...selected];
    if (ids.length === 0) { toast.info("Nothing selected"); return; }
    if (!window.confirm(`Run ${kind} on ${ids.length} run(s)?`)) return;
    const routes = {
      retry:            "bulk-retry",
      cancel:           "bulk-cancel?mode=soft",
      republish:        "bulk-republish",
      "delete-drafts":  "bulk-delete-drafts",
      "publish-drafts": "bulk-publish-drafts",
    };
    setBusy(true);
    try {
      const r = await seoApi.post(`/seo/workflows/runs/${routes[kind]}`, { run_ids: ids });
      toast.success(`${kind} complete: ${JSON.stringify(r.data).slice(0, 100)}…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  function selectByStatus(matcher) {
    const ids = rows.filter(matcher).map((r) => r.id);
    setSelected(new Set(ids));
    toast.info(`Selected ${ids.length} runs`);
  }

  const allSelected = rows.length > 0 && selected.size === rows.length;
  return (
    <div className="space-y-6" data-testid="runs-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Generation Runs</h1>
          <p className="text-neutral-500 text-sm mt-1">Every ACF §6 execution triggered from a brief.</p>
        </div>
        <div className="text-sm text-neutral-500">{selected.size} selected · {rows.length} total</div>
      </div>

      {/* Bulk actions bar */}
      <div className="flex flex-wrap gap-2" data-testid="bulk-actions">
        <button className="text-xs underline text-neutral-600 hover:text-neutral-900"
                  onClick={() => selectByStatus((r) => r.status === "failed")}>
          Select Failed
        </button>
        <button className="text-xs underline text-neutral-600 hover:text-neutral-900"
                  onClick={() => selectByStatus((r) => (r.quality_warnings || []).length > 0)}>
          Select Warnings
        </button>
        <button className="text-xs underline text-neutral-600 hover:text-neutral-900"
                  onClick={() => selectByStatus((r) => r.published_status === "draft")}>
          Select Drafts
        </button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" disabled={busy || selected.size === 0}
                  onClick={() => bulkAction("retry")} data-testid="bulk-retry">
          Retry ({selected.size})
        </Button>
        <Button size="sm" variant="outline" disabled={busy || selected.size === 0}
                  onClick={() => bulkAction("cancel")} data-testid="bulk-cancel">
          Cancel
        </Button>
        <Button size="sm" variant="outline" disabled={busy || selected.size === 0}
                  onClick={() => bulkAction("republish")} data-testid="bulk-republish">
          Republish
        </Button>
        <Button size="sm" variant="outline" disabled={busy || selected.size === 0}
                  onClick={() => bulkAction("publish-drafts")} data-testid="bulk-publish-drafts">
          Publish drafts
        </Button>
        <Button size="sm" variant="destructive" disabled={busy || selected.size === 0}
                  onClick={() => bulkAction("delete-drafts")} data-testid="bulk-delete-drafts">
          Delete drafts
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">No runs yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr className="border-b border-neutral-200">
                  <th className="px-3 py-3">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll}
                            data-testid="select-all" />
                  </th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-left px-4 py-3">Brief</th>
                  <th className="text-left px-4 py-3">Provider / Model</th>
                  <th className="text-left px-4 py-3">Started</th>
                  <th className="text-left px-4 py-3">Published</th>
                  <th className="text-left px-4 py-3">Actor</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t border-neutral-100 ${selected.has(r.id) ? "bg-neutral-50" : ""}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(r.id)}
                                onChange={() => toggle(r.id)}
                                data-testid={`select-${r.id.slice(0, 8)}`} />
                    </td>
                    <td className="px-6 py-3"><Badge variant="outline">{r.status}</Badge></td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {r.brief_id?.slice(0, 8)} v{r.brief_version}
                    </td>
                    <td className="px-4 py-3">{r.provider || "—"} / {r.model || "—"}</td>
                    <td className="px-4 py-3 text-neutral-500">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {r.published_id ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
                          {r.published_id.slice(0, 8)}
                        </Badge>
                      ) : (
                        <span className="text-neutral-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{r.metadata?.actor || "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <Link to={`/seo-studio/runs/${r.id}`} className="underline hover:no-underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function RunDetails() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [content, setContent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  // Editing state
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editNote, setEditNote] = useState("");
  const [selectedVersion, setSelectedVersion] = useState(null); // null = current
  const [showHistory, setShowHistory] = useState(false);
  const editTextareaRef = useRef(null);

  // Autofocus the edit textarea whenever entering edit mode + place caret
  // at the end so keeping typing "just works".
  useEffect(() => {
    if (!editing) return;
    const el = editTextareaRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: false });
      const pos = el.value.length;
      el.setSelectionRange(pos, pos);
    } catch (_e) { el.focus(); }
  }, [editing]);

  async function load() {
    const r = await seoApi.get(`/seo/workflows/runs/${runId}`);
    setRun(r.data);
    if (r.data.status === "success") {
      const c = await seoApi.get(`/seo/workflows/runs/${runId}/content`);
      setContent(c.data);
    }
  }
  useEffect(() => { load(); }, [runId]);
  // Live poll while running (server call is fast; short interval)
  useEffect(() => {
    if (run?.status !== "running" && run?.status !== "queued") return;
    const t = setTimeout(() => { setPollCount((c) => c + 1); load(); }, 2000);
    return () => clearTimeout(t);
  }, [run?.status, pollCount]);

  const versions = content?.versions || [];
  const activeVersion = selectedVersion === null
    ? content?.current_content_version
    : selectedVersion;
  const activeVersionEntry = versions.find((v) => v.version === activeVersion);
  const shownContent = activeVersionEntry?.content ?? content?.content ?? "";
  const viewingLatest = activeVersion === content?.current_content_version;

  function startEditing() {
    setDraft(content?.content || "");
    setEditNote("");
    setSelectedVersion(null); // always edit the latest
    setEditing(true);
  }
  function cancelEditing() {
    setEditing(false);
    setDraft("");
    setEditNote("");
  }

  function copy() {
    if (!shownContent) return;
    navigator.clipboard.writeText(shownContent);
    setCopied(true); setTimeout(() => setCopied(false), 1200);
    toast.success("Copied to clipboard");
  }

  async function save() {
    if (!draft.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const r = await seoApi.post(
        `/seo/workflows/runs/${runId}/content`,
        { content: draft, note: editNote || null },
      );
      setContent(r.data);
      setEditing(false);
      setEditNote("");
      toast.success(`Saved as version ${r.data.current_content_version}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function publish(status) {
    try {
      toast.info(status === "published" ? "Publishing to CMS…" : "Saving draft to CMS…");
      const r = await seoApi.post(`/seo/workflows/runs/${runId}/publish`, { status });
      toast.success(`${status === "published" ? "Published" : "Draft saved"}: ${r.data.published_slug}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  }

  if (!run) return <div className="text-neutral-500">Loading…</div>;
  const durationMs = run.completed_at
    ? new Date(run.completed_at) - new Date(run.started_at) : null;

  return (
    <div className="space-y-5" data-testid="run-details-page">
      <Link to="/seo-studio/runs"
            className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Runs
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Generation run</h1>
          <div className="text-sm text-neutral-500 mt-1 font-mono">{run.id}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={run.status} />
          <Link to="/seo-studio/briefs/new">
            <Button variant="outline" size="sm" data-testid="run-details-new-brief">
              <Plus size={14} /> New Brief
            </Button>
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Metadata</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <M k="Provider" v={run.provider} />
          <M k="Model" v={run.model} />
          <M k="Brief" v={run.brief_id ? `${run.brief_id.slice(0,8)} v${run.brief_version}` : "—"} />
          <M k="ACF history" v={run.acf_history_id ? run.acf_history_id.slice(0,8) : "—"} />
          <M k="Started" v={new Date(run.started_at).toLocaleString()} />
          <M k="Completed" v={run.completed_at ? new Date(run.completed_at).toLocaleString() : "—"} />
          <M k="Duration" v={durationMs ? `${(durationMs/1000).toFixed(1)}s` : "—"} />
          <M k="Latency (ACF)" v={run.latency_ms != null ? `${run.latency_ms} ms` : "—"} />
          <M k="Retries used" v={run.retries_used ?? "—"} />
          <M k="Prompt length" v={run.prompt_length != null ? `${run.prompt_length} chars` : "—"} />
          <M k="Response length" v={run.response_length != null ? `${run.response_length} chars` : "—"} />
          <M
            k={run.token_source === "real" ? "Real tokens" : "Est. tokens"}
            v={
              run.total_tokens != null
                ? `${run.prompt_tokens} + ${run.completion_tokens} = ${run.total_tokens}`
                : run.estimated_total_tokens != null
                  ? `${run.estimated_prompt_tokens} + ${run.estimated_completion_tokens} = ${run.estimated_total_tokens}`
                  : "—"
            }
          />
          <M k="Token source" v={run.token_source === "real"
            ? "Provider (real)"
            : run.token_source === "estimated" ? "Local estimate" : "—"} />
          <M k="Estimated cost" v={run.estimated_cost_usd != null ? `$${run.estimated_cost_usd.toFixed(6)}` : "—"} />
          <M k="Actor" v={run.metadata?.actor || "—"} />
          <M k="Content version" v={run.current_content_version
            ? `v${run.current_content_version}${run.content_versions_count > 1 ? ` (${run.content_versions_count} total)` : ""}`
            : "—"} />
          <M k="Last edited" v={run.last_edited_at
            ? `${new Date(run.last_edited_at).toLocaleString()}${run.last_edited_by ? ` · ${run.last_edited_by}` : ""}`
            : "—"} />
          <M k="Published" v={run.published_id
            ? `${run.published_target}/${run.published_id.slice(0,8)}` : "—"} />
        </CardContent>
      </Card>

      {/* Workflow Control — Phase 2 Block 3 */}
      <WorkflowControls run={run} onChanged={load} />

      {/* Universal Workflow Timeline — Phase 2 Block 2 */}
      <RunTimeline runId={run.id} autoRefresh={run.status === "running"} />

      {run.status === "running" && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <div className="animate-pulse h-2 w-2 bg-amber-500 rounded-full" />
              Generation in progress · polling every 2s
            </div>
          </CardContent>
        </Card>
      )}

      {run.status === "failed" && (
        <Card>
          <CardHeader><CardTitle className="text-base text-rose-700">Failed</CardTitle></CardHeader>
          <CardContent><div className="text-sm">{run.error || "unknown error"}</div></CardContent>
        </Card>
      )}

      {run.status === "success" && run.quality_warnings && run.quality_warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="quality-warnings-card">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm text-amber-900 flex items-center gap-2">
              <AlertTriangle size={16} /> Quality check — {run.quality_warnings.length} warning
              {run.quality_warnings.length !== 1 ? "s" : ""}
              {typeof run.quality_score === "number" && (
                <span className="text-amber-700 font-normal text-xs">
                  · score {Math.round(run.quality_score * 100)}%
                </span>
              )}
            </CardTitle>
            <span className="text-xs text-amber-700">Advisory only — nothing is blocked.</span>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="text-sm text-amber-900 list-disc ml-5 space-y-1">
              {run.quality_warnings.map((w, i) => (
                <li key={i} data-testid={`quality-warning-${i}`}>{w}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {run.status === "success" && content && (
        <Card data-testid="run-content-card">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">
                {editing ? "Editing article" : "Generated article"}
                <span className="ml-2 text-xs text-neutral-500 font-normal">
                  ({shownContent.length} chars · v{activeVersion}
                  {activeVersionEntry?.source === "human" ? " · edited" : " · original"})
                </span>
              </CardTitle>
              {versions.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowHistory((s) => !s)}
                  data-testid="toggle-history-btn"
                >
                  <History size={14} /> {versions.length} versions
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelEditing}
                    disabled={saving}
                    data-testid="cancel-edit-btn"
                  >
                    <X size={14} /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving || !draft.trim()}
                    data-testid="save-edit-btn"
                  >
                    <Save size={14} /> {saving ? "Saving…" : "Save version"}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={copy} data-testid="copy-content-btn">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                  </Button>
                  {viewingLatest && !run.published_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={startEditing}
                      data-testid="edit-content-btn"
                    >
                      <Pencil size={14} /> Edit
                    </Button>
                  )}
                  {!run.published_id ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => publish("draft")}
                              data-testid="publish-draft-btn" disabled={!viewingLatest}>
                        Save as Draft
                      </Button>
                      <Button size="sm" onClick={() => publish("published")}
                              data-testid="publish-live-btn" disabled={!viewingLatest}>
                        Publish to CMS
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2" data-testid="published-actions">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200"
                             data-testid="published-badge">
                        Published · {run.published_id?.slice(0, 8)}
                      </Badge>
                      <a
                        href={`/api/cms/admin/collections/blog-posts/${run.published_id}`}
                        target="_blank" rel="noreferrer noopener"
                        data-testid="open-in-cms-btn"
                      >
                        <Button size="sm" variant="outline">
                          <ExternalLink size={14} /> Open in CMS
                        </Button>
                      </a>
                      <Link
                        to={`/seo-studio/published?q=${encodeURIComponent(run.published_slug || run.published_id)}`}
                        data-testid="open-in-published-btn"
                      >
                        <Button size="sm" variant="outline">
                          <Newspaper size={14} /> View in Published
                        </Button>
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardHeader>

          {showHistory && !editing && (
            <CardContent className="border-t border-neutral-100 bg-neutral-50 py-3">
              <div className="text-xs text-neutral-500 mb-2">
                Version history — click to preview an earlier version. Publishing always uses the latest.
              </div>
              <div className="flex flex-wrap gap-2" data-testid="version-history">
                {[...versions].reverse().map((v) => {
                  const isActive = v.version === activeVersion;
                  return (
                    <button
                      key={v.version}
                      type="button"
                      onClick={() => setSelectedVersion(
                        v.version === content.current_content_version ? null : v.version
                      )}
                      className={`text-xs px-3 py-1.5 rounded border transition ${
                        isActive
                          ? "bg-neutral-900 text-white border-neutral-900"
                          : "bg-white border-neutral-200 hover:border-neutral-400"
                      }`}
                      data-testid={`version-pill-${v.version}`}
                    >
                      <span className="font-medium">v{v.version}</span>
                      <span className="ml-2 opacity-75">{v.source}</span>
                      <span className="ml-2 opacity-60">
                        {new Date(v.saved_at).toLocaleString()}
                      </span>
                      {v.note && <span className="ml-2 opacity-75">· {v.note}</span>}
                    </button>
                  );
                })}
              </div>
              {!viewingLatest && (
                <div className="mt-3 text-xs text-amber-700">
                  Viewing an older version (v{activeVersion}). Switch back to v{content.current_content_version} to edit or publish.
                </div>
              )}
            </CardContent>
          )}

          <CardContent>
            {editing ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
                    Markdown source
                  </label>
                  <textarea
                    ref={editTextareaRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      // Escape cancels the edit (only when the textarea has focus)
                      if (e.key === "Escape" && !saving) {
                        e.preventDefault();
                        cancelEditing();
                      }
                    }}
                    className="min-h-[500px] font-mono text-[13px] leading-relaxed p-4 bg-neutral-50 border border-neutral-200 rounded resize-y focus:outline-none focus:ring-1 focus:ring-neutral-400"
                    data-testid="edit-textarea"
                    spellCheck={false}
                  />
                  <input
                    type="text"
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    placeholder="Optional note (e.g. 'fixed typo in intro')"
                    className="mt-2 text-sm px-3 py-2 border border-neutral-200 rounded focus:outline-none focus:ring-1 focus:ring-neutral-400"
                    data-testid="edit-note"
                    maxLength={500}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
                    Live preview
                  </label>
                  <div
                    className="min-h-[500px] p-4 bg-white border border-neutral-200 rounded overflow-auto prose prose-sm max-w-none"
                    data-testid="edit-preview"
                  >
                    <ReactMarkdown>{draft || "_(empty)_"}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="prose prose-sm max-w-none p-4 bg-white border border-neutral-200 rounded"
                data-testid="run-content-body"
              >
                <ReactMarkdown>{shownContent}</ReactMarkdown>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    success: "bg-emerald-100 text-emerald-800 border-emerald-200",
    queued: "bg-sky-100 text-sky-800 border-sky-200",
    running: "bg-amber-100 text-amber-800 border-amber-200",
    failed: "bg-rose-100 text-rose-800 border-rose-200",
  };
  return <Badge variant="outline" className={map[status]}>{status}</Badge>;
}
function M({ k, v }) {
  return (
    <div>
      <div className="text-neutral-500 text-xs uppercase tracking-wide">{k}</div>
      <div className="mt-1">{v || "—"}</div>
    </div>
  );
}
